<template>
  <div v-if="error" class="error-wrapper">
    <error404
      v-if="Number(error.statusCode) === 404"
      :error="error"
      :contact-us-link="contactUsLink"
    />
    <error500
      v-else-if="Number(error.statusCode) === 500"
      :error="error"
      :contact-us-link="contactUsLink"
    />
    <error-general v-else :error="error" :contact-us-link="contactUsLink" />
    <div v-if="isDev">
      <h2>Error Details</h2>
      <pre>{{ $isDesktop }}</pre>
      <pre>{{ $route.name }}</pre>
      <pre>{{ error.message }}</pre>
      <pre>{{ error.stack }}</pre>
      <pre>{{
        typeof error === 'object' && error !== null
          ? JSON.stringify(error, null, 2)
          : String(error)
      }}</pre>
    </div>
    <nuxt-to-meta :nuxt2meta="head" />
  </div>
</template>

<script lang="ts">
import ErrorCaptured from '~/mixins/ErrorCaptured'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'

@Component({
  layout: 'default',
  mixins: [ErrorCaptured],
})
class ErrorLayout extends Vue {
  get head() {
    return {
      meta: [
        {
          hid: 'robots',
          name: 'robots',
          content: 'noindex',
        },
      ],
    }
  }

  @Prop({ type: Object, required: true }) readonly error: any & {
    httpHeaders?: Record<string, string>
  }

  created() {
    const httpHeaders = this.error.httpHeaders
    if (this.error && typeof httpHeaders !== 'undefined' && process.server) {
      Object.keys(httpHeaders).forEach((key) => {
        httpHeaders[key]
          ? this.$nuxt.context.res.setHeader(key, httpHeaders[key])
          : this.$nuxt.context.res.removeHeader(key)
      })
    }
  }

  mounted() {
    if (Number(this.error.statusCode) === 404) {
      void this.sendBreadcrumb(this.error.message || '')
    } else {
      void this.sendErrorAndOpenFeedbackModal()
    }
    this.togglePageWrapperClass(true)
  }

  beforeDestroy() {
    this.togglePageWrapperClass(false)
  }

  get contactUsLink() {
    // Defensive: $i18n may not be initialized if hydration fails
    const locale = this.$i18n?.locale ?? 'en-gb'
    return `https://www.pitchup.com/${
      locale !== 'en-gb' ? locale + '/' : ''
    }about/contact/`
  }

  private get errorEvent() {
    const error = new Error(this.error.message)
    error.name = `Error ${this.error.statusCode}: ${this.error.message}`
    return error
  }

  private async sendErrorAndOpenFeedbackModal() {
    if (typeof this.$sentryReady === 'undefined') {
      // no sentry

      console.error(this.error)
      return
    }
    const sentryReadyInstance = await this.$sentryReady()
    const eventId = sentryReadyInstance.captureException(this.errorEvent)
    if (eventId) {
      // @ts-ignore
      sentryReadyInstance.showReportDialog({ eventId })
    }
  }

  private async sendBreadcrumb(message: string) {
    if (typeof this.$sentryReady === 'undefined') {
      // no sentry

      console.error(this.error)
      return
    }
    const sentryReadyInstance = await this.$sentryReady()
    sentryReadyInstance.addBreadcrumb({
      category: '404',
      message,
      level: 'error',
    })
  }

  private togglePageWrapperClass(hasWrapper: boolean) {
    const pageWrapperEl = document.querySelector('#page-wrapper')
    if (!pageWrapperEl) {
      return
    }

    if (hasWrapper) {
      pageWrapperEl.classList.add('error-page-wrapper')
    } else {
      pageWrapperEl.classList.remove('error-page-wrapper')
    }
  }

  get isDev() {
    return process.env.NODE_ENV !== 'production'
  }
}

export default toNative(ErrorLayout)
</script>

<style scoped>
.error-wrapper {
  line-height: 1.4;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  font-size: var(--body);
  justify-content: flex-start;
  padding: 1.4em;
}

.error-wrapper :deep(.header) {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 0 0 1em 0;
}

.error-wrapper :deep(.header h2) {
  margin: 0 0 0 0.5em;
  color: var(--colour-blue);
}

.error-wrapper :deep(.error > a) {
  display: block;
  margin-top: 1em;
}
</style>

<style>
.error-page-wrapper {
  min-height: 100vh !important;
}
</style>
